/**
 * Les caractères typographiques que le TEXTE sait déjà rendre.
 *
 * C'est la frontière basse du moteur, et elle est utile : tant qu'une
 * expression tient en caractères Unicode réels (`4²`, `x₁`, `≤`), le texte
 * reste supérieur au dessin — il se coupe en fin de ligne, il suit la taille
 * de police du système, il se copie. On ne dessine qu'au-delà de cette
 * frontière.
 *
 * Ces tables reprennent, à l'identique, celles du composant `TexteMath` de
 * l'app (Sprint 19) : elles sont remontées ici parce que la DÉCISION de
 * dessiner ou non en dépend, et que cette décision doit être la même dans
 * l'app et dans le rendu du courriel.
 */
/** Exposants Unicode. Couverture volontairement limitée : chiffres, signes, `n`/`i`. */
const EXPOSANTS = {
    '0': '⁰',
    '1': '¹',
    '2': '²',
    '3': '³',
    '4': '⁴',
    '5': '⁵',
    '6': '⁶',
    '7': '⁷',
    '8': '⁸',
    '9': '⁹',
    '+': '⁺',
    '-': '⁻',
    '−': '⁻',
    '=': '⁼',
    '(': '⁽',
    ')': '⁾',
    n: 'ⁿ',
    i: 'ⁱ',
};
/** Indices Unicode — même couverture. */
const INDICES = {
    '0': '₀',
    '1': '₁',
    '2': '₂',
    '3': '₃',
    '4': '₄',
    '5': '₅',
    '6': '₆',
    '7': '₇',
    '8': '₈',
    '9': '₉',
    '+': '₊',
    '-': '₋',
    '−': '₋',
    '=': '₌',
    '(': '₍',
    ')': '₎',
    n: 'ₙ',
};
function convertirTous(brut, table) {
    if (brut.length === 0)
        return null;
    let resultat = '';
    for (const caractere of brut) {
        const converti = table[caractere];
        if (converti === undefined)
            return null;
        resultat += converti;
    }
    return resultat;
}
/** La forme exposant Unicode de `brut`, ou `null` si un seul caractère manque. */
export const enExposantUnicode = (brut) => convertirTous(brut, EXPOSANTS);
/** La forme indice Unicode de `brut`, ou `null` si un seul caractère manque. */
export const enIndiceUnicode = (brut) => convertirTous(brut, INDICES);
/** Barre de fraction typographique (U+2044), pas un `/` de clavier. */
const BARRE_FRACTION = '⁄';
/**
 * Les relations tapées au clavier, rendues dans leur VRAI caractère. `<=`
 * n'existe pas en français : le substituer ne peut donc rien casser, et une
 * inéquation du secondaire s'écrit `≤`, pas « inférieur ou égal » en ASCII.
 */
const RELATIONS = [
    [/<=/g, '≤'],
    [/>=/g, '≥'],
    [/!=/g, '≠'],
];
/**
 * Repère `sqrt(...)`, `base^exposant` et `numérateur/dénominateur` (jusqu'à
 * `base^exposant/dénominateur`, ex. `x^2/3`) et les remplace par leur forme
 * Unicode typographiée. Tout le reste du texte est laissé tel quel.
 *
 * C'est le REPLI du moteur, et le comportement HISTORIQUE de l'app : tout ce
 * que `analyserTexteMath` laisse en prose passe par ici, donc tout ce qui
 * s'affichait correctement continue de s'afficher de la même façon. Il vit
 * dans `@silex/shared` avec le reste du moteur pour que le rendu du courriel
 * au parent dégrade EXACTEMENT comme l'app, et pas autrement.
 */
export function formaterMathTexte(texte) {
    const avecRelations = RELATIONS.reduce((acc, [motif, remplacement]) => acc.replace(motif, remplacement), texte);
    const sansRacines = avecRelations.replace(/sqrt\(([^()]*)\)/gi, (_correspondance, contenu) => `√(${contenu})`);
    return sansRacines.replace(/([A-Za-zπ0-9]+)\^(\{[^{}]+\}|[A-Za-zπ0-9]+)(?:\/(\d+))?|(\d+)\/(\d+)/g, (correspondance, base, exposantBrut, denomApresExposant, numFraction, denomFraction, position, entier) => {
        if (base !== undefined) {
            const brut = exposantBrut;
            const sansAccolades = brut.startsWith('{') && brut.endsWith('}') ? brut.slice(1, -1) : brut;
            const exposant = enExposantUnicode(sansAccolades);
            if (exposant === null)
                return correspondance; // non convertible -> inchangé
            if (denomApresExposant === undefined)
                return `${base}${exposant}`;
            const indice = enIndiceUnicode(denomApresExposant);
            if (indice === null)
                return correspondance;
            return `${base}${exposant}${BARRE_FRACTION}${indice}`;
        }
        // Une DATE n'est pas une fraction. `12/06/2026` donnait jusqu'ici
        // « ¹²⁄₀₆/2026 » : le défaut existait avant ce moteur, il se voyait
        // seulement moins. Une barre oblique ou un tiret qui touche le motif
        // des deux côtés dit qu'on est au milieu d'autre chose.
        const avant = position === 0 ? '' : entier[position - 1];
        const apres = entier[position + correspondance.length];
        if (avant === '/' || avant === '-' || apres === '/' || apres === '-') {
            return correspondance;
        }
        const exposantNum = enExposantUnicode(numFraction);
        const indiceDen = enIndiceUnicode(denomFraction);
        if (exposantNum === null || indiceDen === null)
            return correspondance;
        return `${exposantNum}${BARRE_FRACTION}${indiceDen}`;
    });
}
//# sourceMappingURL=unicode.js.map